/** @jsxImportSource react */
import { useState } from 'react'
import { Alert, Button, Paper, Stack, Typography } from '@mui/material'
import GoogleIcon from '@mui/icons-material/Google'
import MicrosoftIcon from '@mui/icons-material/Microsoft'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import { useMutation, useQuery } from '@tanstack/react-query'
import { emailRepliesAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { getErrorMessage } from '../../utils/apiErrors'

const NAMES = { GOOGLE: 'Google Calendar', MICROSOFT: 'Outlook calendar' }
const ICONS = { GOOGLE: GoogleIcon, MICROSOFT: MicrosoftIcon }
const SIGNED_IN_WITH = { google: 'GOOGLE', microsoft: 'MICROSOFT' }

const read = (key) => {
  try { return localStorage.getItem(key) } catch { return null }
}

/**
 * "Connect your calendar", when the installation can and yours isn't connected yet. The provider
 * you signed in with comes first; Google then usually asks one short question, with your account
 * already chosen. {@code variant}: "card" (home page, can be dismissed for good) or "inline"
 * (meeting dialog).
 */
const ConnectCalendarPrompt = ({ variant = 'card' }) => {
  const tenantId = useTenantKey()
  const dismissKey = `calendarPromptDismissed:${tenantId}`
  const [dismissed, setDismissed] = useState(() => read(dismissKey) === '1')
  const { data } = useQuery({
    queryKey: [tenantId, 'email-replies', 'mailboxes'],
    queryFn: emailRepliesAPI.mailboxes,
    enabled: !!tenantId && !(variant === 'card' && dismissed),
    staleTime: 300_000,
  })
  const connect = useMutation({
    mutationFn: (provider) => emailRepliesAPI.connect(provider, 'CALENDAR'),
    onSuccess: ({ url }) => window.location.assign(url),   // the provider's page, then back to Settings → Email & calendar
  })
  const available = data?.available || []
  const connected = (data?.mine || []).some((c) => c.purposes.includes('CALENDAR') && c.status !== 'ERROR')
  if (!data || !available.length || connected || (variant === 'card' && dismissed)) return null
  const first = SIGNED_IN_WITH[read('signedInWith')]
  const providers = [...available].sort((a, b) => (a === first ? -1 : b === first ? 1 : 0))
  const buttons = providers.map((p) => {
    const Icon = ICONS[p]
    return (
      <Button key={p} size="small" variant={p === providers[0] ? 'contained' : 'outlined'} startIcon={Icon && <Icon />}
        disabled={connect.isPending} onClick={() => connect.mutate(p)}>
        Connect {NAMES[p] || p}
      </Button>
    )
  })

  if (variant === 'inline') {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <Typography variant="caption" color="text.secondary">Check your own calendar too:</Typography>
        {buttons}
        {connect.error && <Typography variant="caption" color="error">{getErrorMessage(connect.error)}</Typography>}
      </Stack>
    )
  }
  const dismiss = () => {
    try { localStorage.setItem(dismissKey, '1') } catch { /* private window: it comes back next time */ }
    setDismissed(true)
  }
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        <CalendarMonthOutlinedIcon color="primary" />
        <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle2">Connect your calendar</Typography>
          <Typography variant="body2" color="text.secondary">
            See your own events next to your CRM meetings, avoid double-booking, and let meetings you plan go straight into your
            calendar with the invites. Teammates only see when you’re busy, never what.
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0, flexWrap: 'wrap', rowGap: 1 }}>
          {buttons}
          <Button size="small" color="inherit" onClick={dismiss}>Not now</Button>
        </Stack>
      </Stack>
      {connect.error && <Alert severity="error" sx={{ mt: 1.5 }}>{getErrorMessage(connect.error)}</Alert>}
    </Paper>
  )
}

export default ConnectCalendarPrompt
